import { IconAlertTriangle, IconX } from '@tabler/icons-react';
import { useState } from 'react';

import { useAuth } from 'hooks/useAuth';
import { formatLongDate } from 'util/formatters';

// Fixed platform-wide threshold — mirrors
// app/tasks/subscription.py::_WARNING_DAYS_BEFORE_END on the backend (not a
// per-org setting, so the two are kept in step by convention, not by a shared
// config value).
const WARNING_DAYS = 10;

/**
 * Global "subscription ending soon" bar — the frontend half of
 * app/tasks/subscription.py::send_subscription_expiry_warnings (that task
 * covers the email + bell notification; this covers the persistent banner).
 *
 * Sourced from `GET /users/profile`'s `subscription` block (see
 * app/routers/users.py), already fetched into `useAuth().user` on every page
 * load — no extra request. Dismissing is per-tab only (sessionStorage), keyed
 * by end_date, so a renewal that pushes the date out re-arms it automatically
 * instead of staying dismissed forever.
 */
export default function SubscriptionBanner() {
  const { user } = useAuth();
  const subscription = user?.subscription;
  const [dismissedKey, setDismissedKey] = useState(() => {
    try {
      return sessionStorage.getItem('subscriptionBannerDismissed') || '';
    } catch {
      return '';
    }
  });

  if (!subscription || subscription.status !== 'active') return null;
  const { end_date: endDate, days_remaining: daysRemaining } = subscription;
  if (daysRemaining == null || daysRemaining > WARNING_DAYS) return null;
  if (dismissedKey === endDate) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem('subscriptionBannerDismissed', endDate);
    } catch {
      // Private-browsing / storage disabled — dismissal just won't persist
      // for the rest of the tab session, which is fine, not fatal.
    }
    setDismissedKey(endDate);
  };

  const message =
    daysRemaining >= 0
      ? `Your organization's subscription ends in ${daysRemaining} day${daysRemaining === 1 ? '' : 's'} (${formatLongDate(endDate)}).`
      : `Your organization's subscription ended on ${formatLongDate(endDate)} and is in its grace period.`;

  return (
    <div className="flex items-center gap-3 border-b border-warning/30 bg-warning/5 px-4 py-2.5 text-sm text-warning sm:px-6 lg:px-10">
      <IconAlertTriangle className="h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 truncate">{message} Contact the platform administrator to renew.</span>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded-full p-1 text-warning/70 hover:bg-warning/10 hover:text-warning"
      >
        <IconX className="h-4 w-4" />
      </button>
    </div>
  );
}
